// client/src/pages/Login.js (CLIENT – clean, storm-proof)

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import "./Login.css";
import "../styles/CpherePrimaryAction.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";

const AUTH_TRANSITION_KEY = "cortexAuthTransition";
const LOGOUT_IN_PROGRESS_KEY = "cortexLogoutInProgress";
const LOGOUT_MODE_KEY = "cortexLogoutMode";
const LAST_LOGOUT_TS_KEY = "cortexLastLogoutTs";
const PASSWORD_CHANGE_SUCCESS_KEY = "cpherePasswordChangeSuccess";
const PASSWORD_CHANGE_EXPIRED_KEY = "cpherePasswordChangeExpired";
const PASSWORD_CHANGE_PENDING_RELOAD = "pending-reload";
const PASSWORD_CHANGE_ARMED_RELOAD = "armed-reload";
const CPHERE_HOMEPAGE_URL = "https://www.cphere.ai/platform-access";

const normalizeBase = (base) => {
  if (!base) return "";
  let b = String(base).trim();
  b = b.replace(/\/+$/, "");
  if (b.toLowerCase().endsWith("/api")) b = b.slice(0, -4);
  return b;
};

const API_BASE_URL =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "https://cphere-backend.onrender.com";

const normalizeRole = (val) =>
  String(val || "")
    .toLowerCase()
    .trim()
    .replace(/[_\s]/g, "");

const getLandingPathForRole = (role) =>
  normalizeRole(role) === "creative" ? "/nexus-hub" : "/dashboard";

const getLoginMessage = (status, data) => {
  const backendMsg = (data?.error || data?.message || "").trim();
  if (status === 401) return "Invalid email or password.";
  if (status === 403) return "You do not have access to the Client portal.";
  if (status === 404) return "Login service not found. Please contact support.";
  if (status >= 500) return "Server error. Please try again.";
  return backendMsg || "Login failed. Please try again.";
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession, logout } = useAuth() || {};
  const passwordChangeMarkerHandledRef = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(() => {
    try {
      const marker = sessionStorage.getItem(PASSWORD_CHANGE_SUCCESS_KEY);
      return marker === PASSWORD_CHANGE_PENDING_RELOAD || marker === PASSWORD_CHANGE_ARMED_RELOAD;
    } catch { return false; }
  });
  const [passwordChangeExpired, setPasswordChangeExpired] = useState(() => {
    try {
      const marker = sessionStorage.getItem(PASSWORD_CHANGE_EXPIRED_KEY);
      return marker === PASSWORD_CHANGE_PENDING_RELOAD || marker === PASSWORD_CHANGE_ARMED_RELOAD;
    } catch { return false; }
  });

  const didNavigateRef = useRef(false);

  useEffect(() => {
    const clearLogoutFlags = () => {
      try {
        localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
        localStorage.removeItem(LOGOUT_MODE_KEY);
        localStorage.removeItem(LAST_LOGOUT_TS_KEY);
      } catch {}
      try {
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
        sessionStorage.removeItem(LOGOUT_MODE_KEY);
        sessionStorage.removeItem(LAST_LOGOUT_TS_KEY);
      } catch {}
    };

    clearLogoutFlags();

    if (String(location.search || "").includes("logout=1")) {
      navigate("/login", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    // React StrictMode replays effects in development. A pending marker belongs
    // to the transient SPA login rendered while logout is in progress, so leave
    // it untouched. AuthContext arms it immediately before the hard navigation;
    // only that post-navigation mount consumes it.
    if (passwordChangeMarkerHandledRef.current) return;
    passwordChangeMarkerHandledRef.current = true;
    try {
      for (const key of [PASSWORD_CHANGE_SUCCESS_KEY, PASSWORD_CHANGE_EXPIRED_KEY]) {
        if (sessionStorage.getItem(key) === PASSWORD_CHANGE_ARMED_RELOAD) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {}
  }, []);

  const handleLogin = async () => {
    setPasswordChanged(false);
    setPasswordChangeExpired(false);
    try {
      sessionStorage.removeItem(PASSWORD_CHANGE_SUCCESS_KEY);
      sessionStorage.removeItem(PASSWORD_CHANGE_EXPIRED_KEY);
    } catch {}
    const em = String(email || "").trim();
    // Passwords are opaque values. Preserve whitespace exactly so every
    // password accepted by the change endpoint can be used to sign in.
    const pw = String(password || "");

    if (!em || !pw) {
      setErrorMessage("Email and password cannot be empty.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    didNavigateRef.current = false;

    try {
      sessionStorage.setItem(AUTH_TRANSITION_KEY, "1");
    } catch {}

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: em, password: pw }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        try {
          sessionStorage.removeItem(AUTH_TRANSITION_KEY);
        } catch {}
        setErrorMessage(getLoginMessage(response.status, data));
        setLoading(false);
        return;
      }

      const token = String(data?.token || "").trim();
      const roleRaw = data?.role || "";
      const userEmail = String(data?.userEmail || data?.email || em).trim();

      if (!token) {
        try {
          sessionStorage.removeItem(AUTH_TRANSITION_KEY);
        } catch {}
        setErrorMessage("Login failed: missing token.");
        setLoading(false);
        return;
      }

      const normalizedRole = normalizeRole(roleRaw);

      if (normalizedRole !== "client" && normalizedRole !== "creative") {
        if (typeof logout === "function") await logout();
        try {
          sessionStorage.removeItem(AUTH_TRANSITION_KEY);
        } catch {}
        setErrorMessage("You do not have access to the Client portal.");
        setLoading(false);
        return;
      }

      if (typeof setSession === "function") {
        setSession({
          token,
          email: userEmail,
          role: normalizedRole,
          user: data?.user || null,
          advertisers: Array.isArray(data?.advertiserObjects) ? data.advertiserObjects : [],
        });
      } else {
        localStorage.setItem("clientAuthToken", token);
        localStorage.setItem("authToken", token);
        localStorage.setItem("email", userEmail);
        localStorage.setItem("userEmail", userEmail);
        localStorage.setItem("role", normalizedRole);
      }

      try {
        sessionStorage.removeItem(AUTH_TRANSITION_KEY);
      } catch {}

      didNavigateRef.current = true;
      navigate(getLandingPathForRole(normalizedRole), { replace: true });
    } catch (err) {
      console.error("Unexpected login error:", err);
      try {
        sessionStorage.removeItem(AUTH_TRANSITION_KEY);
      } catch {}
      setErrorMessage("An unexpected error occurred. Please try again.");
      setLoading(false);
    } finally {
      if (!didNavigateRef.current) setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!loading) handleLogin();
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <CphereHeaderLogo
          alt="Cphere"
          className="logo"
          onClick={() => window.open(CPHERE_HOMEPAGE_URL, "_blank", "noopener,noreferrer")}
          style={{ cursor: "pointer" }}
        />

        <div className="cortex-hero-container login-hero">
          <div className="cortex-hero-pill">
            <span className="cortex-hero-pill-label cphere-hero-pill-wordmark">Cortex</span>
            <span className="cortex-hero-pill-tag cphere-hero-pill-tag">OS</span>
          </div>
          <div className="cortex-hero-shimmer-bar" />
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            autoComplete="email"
            disabled={loading}
          />

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            autoComplete="current-password"
            disabled={loading}
          />

          <button
            type="submit"
            className={`login-btn cphere-primary-action${loading ? " is-loading" : ""}`}
            disabled={loading}
            aria-busy={loading}
          >
            <span className="login-btn-copy" aria-live="polite">
              <span
                className="login-btn-label login-btn-label-idle"
                aria-hidden={loading}
              >
                Login
              </span>
              <span
                className="login-btn-label login-btn-label-loading"
                aria-hidden={!loading}
              >
                Logging in...
              </span>
            </span>
          </button>

          <a
            href={CPHERE_HOMEPAGE_URL}
            className="login-secondary-link learn-more-button"
            target="_blank"
            rel="noreferrer noopener"
          >
            Learn More
          </a>

          {errorMessage && (
            <div className="auth-inline-message is-error">{errorMessage}</div>
          )}
          {passwordChanged && (
            <div className="auth-inline-message is-success" role="status">
              Password updated. Sign in with your new password.
            </div>
          )}
          {passwordChangeExpired && (
            <div className="auth-inline-message is-error" role="alert">
              Your session expired before the password could be updated. Sign in and try again.
            </div>
          )}
        </form>

        <div className="login-auxiliary-links">
          <a href="/forgot-password" className="forgot-password-link">
            Forgot Password
          </a>

          <a href="/pantheon" className="pantheon-entry-link">
            EXPLORE THE PANTHEON
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
